/**
 * Shared, memoised derivation of everything a project screen needs.
 *
 * Reads are local, so the only cost of opening a project is recomputing its tallies. That
 * work is done once per replica version and cached here, which is what makes warming it
 * ahead of a navigation worth doing: by the time the pointer lands, the result already
 * exists and the screen renders from cache.
 *
 * Every screen in a project reads the same bundle, so they cannot disagree about which days
 * are sessions or what the roster is.
 */

import { useMemo } from 'react';

import { addDays } from '../../shared/dates.js';
import { indexNotes, type NoteIndex } from '../../shared/notes.js';
import { indexOverrides, listAllSessionDates, type OverrideIndex } from '../../shared/schedule.js';
import {
  indexMarks,
  summarize,
  tallyPeople,
  tallySessions,
  type GroupSummary,
  type MarkIndex,
  type PersonTally,
  type SessionTally,
} from '../../shared/stats.js';
import type { IsoDate, Person, Project, ProjectId } from '../../shared/types.js';
import { useToday } from '../clock.js';
import type { AppStore } from './store.js';
import { useReplicaVersion, useStore } from './context.js';

/** How far past today the session navigation reaches, so a future roll call is reachable. */
const FORWARD_HORIZON_DAYS = 365;

/** Everything the project screens read, derived once per replica version and day. */
export interface ProjectDerived {
  project: Project;
  /** The day this bundle was derived for; every date comparison on screen uses it. */
  day: IsoDate;
  /** Live roster in name order. */
  people: Person[];
  overrides: OverrideIndex;
  marks: MarkIndex;
  /** Everything written down about this project's sessions and the people in them. */
  notes: NoteIndex;
  /** Sessions up to and including `day`, ascending. These are the ones statistics count. */
  pastSessions: IsoDate[];
  /** Sessions up to a year past `day`, ascending. Used to navigate to a future roll call. */
  allSessions: IsoDate[];
  sessionTallies: SessionTally[];
  personTallies: PersonTally[];
  summary: GroupSummary;
  /** Most recent session that has happened, or `null` when none has. */
  lastSession: IsoDate | null;
}

/** How many derived bundles are kept before the least recently used one is dropped. */
const CACHE_LIMIT = 8;

/** Cache key: a bundle is only valid for one replica version, one day, and one rate basis. */
function cacheKey(projectId: ProjectId, version: number, day: IsoDate, countExcusedAsPresent: boolean): string {
  return `${projectId}:${version}:${day}:${countExcusedAsPresent ? 'excused-present' : 'excused-absent'}`;
}

/** Insertion-ordered cache; the oldest entry is evicted once `CACHE_LIMIT` is exceeded. */
const cache = new Map<string, ProjectDerived>();

/** Compares people by display name, case-insensitively. */
function byName(left: Person, right: Person): number {
  return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
}

/** Computes the derived bundle for one project from the current replica contents. */
function compute(store: AppStore, project: Project, day: IsoDate): ProjectDerived {
  const people = [...store.replica.people.values()]
    .filter((person) => person.projectId === project.id && person.deletedAt === null)
    .sort(byName);
  const overrides = indexOverrides(
    [...store.replica.sessionOverrides.values()].filter((override) => override.projectId === project.id),
  );
  const marks = indexMarks(
    [...store.replica.attendanceMarks.values()].filter((mark) => mark.projectId === project.id),
  );
  const notes = indexNotes(
    [...store.replica.notes.values()].filter((note) => note.projectId === project.id),
  );
  const allSessions = listAllSessionDates(project, overrides, addDays(day, FORWARD_HORIZON_DAYS));
  const pastSessions = allSessions.filter((date) => date <= day);
  const sessionTallies = tallySessions(people, pastSessions, marks, project.countExcusedAsPresent);
  const personTallies = tallyPeople(people, pastSessions, marks, project.countExcusedAsPresent);

  return {
    project,
    day,
    people,
    overrides,
    marks,
    notes,
    pastSessions,
    allSessions,
    sessionTallies,
    personTallies,
    summary: summarize(sessionTallies, people.length, project.countExcusedAsPresent),
    lastSession: pastSessions.at(-1) ?? null,
  };
}

/**
 * Returns the derived bundle for `projectId` on `day`, computing it on a miss.
 *
 * @returns the bundle, or `null` when the project is unknown or deleted on this device.
 */
export function deriveProject(store: AppStore, projectId: ProjectId, day: IsoDate): ProjectDerived | null {
  const project = store.replica.projects.get(projectId);
  if (project === undefined || project.deletedAt !== null) return null;

  const key = cacheKey(projectId, store.getVersion(), day, project.countExcusedAsPresent);
  const cached = cache.get(key);
  if (cached !== undefined) {
    // Re-insert so the entry counts as recently used under the eviction order.
    cache.delete(key);
    cache.set(key, cached);
    return cached;
  }

  const derived = compute(store, project, day);
  cache.set(key, derived);
  while (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next();
    if (oldest.done === true) break;
    cache.delete(oldest.value);
  }
  return derived;
}

/** Returns true when `projectId` already has a bundle for the current replica version and day. */
export function isWarm(store: AppStore, projectId: ProjectId, day: IsoDate): boolean {
  const project = store.replica.projects.get(projectId);
  if (project === undefined) return false;
  return cache.has(cacheKey(projectId, store.getVersion(), day, project.countExcusedAsPresent));
}

/** Subscribes to the replica and the clock, and returns the derived bundle for `projectId`. */
export function useProjectDerived(projectId: ProjectId | undefined): ProjectDerived | null {
  const store = useStore();
  const version = useReplicaVersion();
  const day = useToday();
  return useMemo(
    () => (projectId === undefined ? null : deriveProject(store, projectId, day)),
    [store, projectId, version, day],
  );
}
